/* eslint-disable jsx-a11y/anchor-is-valid */
import { IonSpinner, IonToast, useIonToast } from '@ionic/react';
import { Business, objectEquals } from 'model';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useDeleteBusinessMutation, useGetBusinessQuery, useUpdateBusinessMutation } from '../../api/BusinessApi';
import { BusinessEditFields } from '../../components/BusinessEditFields';
import { BusinessHeader } from '../../components/BusinessHeader';
import { Header } from '../../components/Header';
import { IconButton } from '../../components/IconButton';
import { IconCheck, IconClose } from '../../components/QuirkIcons';
import { QuirkIonContent } from '../../components/QuirkIonContent';
import { QuirkIonPage } from '../../components/QuirkIonPage';
import { BusinessEditRules, BusinessWithSimpleItems } from '../../formConfigs/EditBusinessFieldsConfig';
import { useFormValues } from '../../hooks/useFormValues';
import { useUserHasRole } from '../../hooks/useUserHasRole';
import { AdminOnly } from './AdminOnly';

function convertItemsToString(business: Business): BusinessWithSimpleItems {
	return {
		...business,
		itemString: business.items.map(item => item.name).join(', ')
	};
}
function convertStringToItems(business: BusinessWithSimpleItems): Business {
	return {
		...business,
		items: business.itemString.split(',').map(item => ({ type: 'item', name: item.trim() }))
	};
}
export const EditBusiness: React.FC = () => {
	const { businessid, type } = useParams<{ businessid: Business['_id']; type: 'approve' | 'edit' }>();
	const { data } = useGetBusinessQuery(businessid);
	const [deleteBusiness] = useDeleteBusinessMutation();
	const [business, setBusiness] = useState<BusinessWithSimpleItems | undefined | null>(null);
	const {
		setValues,
		formValues,
		stateDataObject: editBusiness,
		validateForm
	} = useFormValues(BusinessEditRules, business, objectEquals);
	const [updateBusiness, { isError: updateIsError, isLoading: updateIsLoading, error: updateError }] =
		useUpdateBusinessMutation();
	const [showToast] = useIonToast();
	useEffect(() => {
		if (data) {
			setBusiness(convertItemsToString(data));
		}
	}, [data]);

	const history = useHistory();
	const admin = useUserHasRole('admin');
	const onSubmit = async () => {
		const compiledNoErrors = validateForm();
		if (!compiledNoErrors) {
			showToast({
				color: 'danger',
				message: 'Please fix errors',
				position: 'top',
				duration: 1500
			});
		}

		if (formValues && business && editBusiness && compiledNoErrors) {
			const data = { ...(convertStringToItems(editBusiness) as Business), draft: false };
			if (type === 'approve') {
				if (data.owner !== '' && data.claimStatus === 'pending') {
					data.claimStatus = 'claimed';
				}
				if (data.submittedBy !== '' && data.submissionStatus === 'pending') {
					data.submissionStatus = 'approved';
				}
			}
			const result = await updateBusiness({
				business: data
			});
			if (!('error' in result)) {
				setBusiness(convertItemsToString(result.data));
				showToast({
					color: 'success',
					message: 'Business has been updated',
					position: 'top',
					duration: 1500
				});
				history.go(-1);
			} else {
				showToast({
					color: 'danger',
					message: 'Error updating business',
					position: 'top',
					duration: 1500
				});
			}
		}
	};
	// Clean up draft business if user cancels
	const onCancel = async () => {
		history.go(-1);

		if (data && data.draft) {
			await deleteBusiness(data._id);
		}
	};

	return (
		<QuirkIonPage name="Edit Business" data={{ business: businessid }}>
			<Header
				title="Edit Business"
				left={
					<IconButton
						icon={<IconClose />}
						onClick={() => {
							onCancel();
						}}
					/>
				}
				right={
					admin ? (
						<IconButton fill="solid" label="Done" icon={<IconCheck />} onClick={() => onSubmit()} />
					) : (
						<div></div>
					)
				}
			/>
			<QuirkIonContent flush padding>
				<AdminOnly>
					<>
						{data && (
							<BusinessHeader
								business={{
									...data,
									bannerURL: formValues?.bannerURL?.value || '',
									logoURL: formValues?.logoURL?.value || '',
									logoBackgroundColor: formValues?.logoBackgroundColor?.value || ''
								}}
							/>
						)}
						<div>
							{business && formValues && (
								<BusinessEditFields
									business={data as BusinessWithSimpleItems}
									setValues={setValues}
									formValues={formValues}
									onSubmit={onSubmit}
								/>
							)}

							<IonSpinner name="crescent" hidden={!updateIsLoading} />
							<IonToast
								isOpen={updateIsError}
								duration={1500}
								message={(updateError as any)?.data}
								position="top"
								color="danger"
							/>
						</div>
					</>
				</AdminOnly>
			</QuirkIonContent>
		</QuirkIonPage>
	);
};
